import { CSSProperties } from "react";

export const styles = {
  container: (isDarkMode: boolean): CSSProperties => ({
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    backgroundColor: isDarkMode ? "#1a1a1a" : "#f9fafb",
    overflow: "hidden",
    position: "fixed",
    top: 0,
    left: 0,
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  }),

  header: (isDarkMode: boolean): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    padding: "10px 20px",
    borderBottom: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
    zIndex: 10,
    flexShrink: 0,
    minHeight: "48px",
  }),

  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    flex: 1,
    minWidth: 0, // Allow flexbox to shrink
  } as CSSProperties,

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: 1,
    minWidth: 0,
  } as CSSProperties,

  titleGroup: {
    display: "flex",
    alignItems: "baseline",
    gap: "12px",
    minWidth: 0, // Allow text truncation
    overflow: "hidden",
  } as CSSProperties,

  title: (isDarkMode: boolean): CSSProperties => ({
    margin: 0,
    color: isDarkMode ? "#ffffff" : "#111827",
    fontSize: "16px",
    fontWeight: 600,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),

  subtitle: (isDarkMode: boolean): CSSProperties => ({
    color: isDarkMode ? "#94a3b8" : "#6b7280",
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),

  buttonGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
  } as CSSProperties,

  iconButton: (isDarkMode: boolean): CSSProperties => ({
    padding: "6px",
    width: "32px",
    height: "32px",
    backgroundColor: "transparent",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.2)"
      : "1px solid rgba(0, 0, 0, 0.2)",
    borderRadius: "4px",
    color: isDarkMode ? "#94a3b8" : "#6b7280",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),

  terminalContainer: (isDarkMode: boolean): CSSProperties => ({
    flex: "1 1 0",
    minHeight: 0, // Important for flex children
    backgroundColor: isDarkMode ? "#0c0c0c" : "#ffffff",
    margin: 0,
    padding: 0,
    overflow: "hidden",
    border: "none",
    outline: "none",
  }),
};

export const globalStyles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  html, body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    height: 100vh;
    width: 100vw;
    /* Prevent zoom on double tap on iOS */
    touch-action: manipulation;
    /* Prevent text selection on mobile */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  
  #root {
    height: 100vh;
    width: 100vw;
    overflow: hidden;
  }
  
  .xterm {
    height: 100% !important;
    padding: 8px !important;
    /* Enable text selection in terminal */
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
    user-select: text !important;
  }
  
  .xterm .xterm-viewport {
    overflow-y: auto !important;
    overflow-x: hidden !important;
    /* xterm 6 setzt hier hartes #000 und synct es nicht mehr mit dem Theme —
       transparent lassen, damit der theme-bewusste Container durchscheint. */
    background-color: transparent !important;
  }
  
  .xterm-screen {
    height: 100% !important;
  }
  
  /* Mobile responsive styles */
  @media (max-width: 767px) {
    /* Ensure container doesn't overflow viewport */
    .terminal-container {
      height: 100vh !important;
      height: -webkit-fill-available !important;
      max-height: 100vh !important;
    }
    
    .xterm {
      padding: 6px !important;
      font-size: 12px !important;
    }
    
    .xterm .xterm-rows {
      line-height: 1.1 !important;
    }
    
    /* Mobile-specific header adjustments */
    .terminal-header {
      padding: 8px 12px !important;
      gap: 10px !important;
    }
    
    .terminal-header-left {
      gap: 10px !important;
    }
    
    .terminal-title-group {
      gap: 8px !important;
    }
    
    .terminal-title {
      font-size: 14px !important;
    }
    
    .terminal-subtitle {
      font-size: 11px !important;
    }
    
    .terminal-button-group {
      gap: 4px !important;
    }
    
    .terminal-icon-button {
      width: 28px !important;
      height: 28px !important;
      padding: 6px !important;
    }
  }
  
  @media (min-width: 768px) {
    .xterm {
      padding: 12px !important;
    }
  }
  
  /* Touch optimizations */
  @media (hover: none) and (pointer: coarse) {
    /* Increase touch targets on mobile */
    button {
      min-height: 44px;
      min-width: 44px;
    }
  }
  
  /* Footer responsive styles */
  @media (min-width: 768px) {
    .footer-support-label-mobile {
      display: none !important;
    }
    .footer-support-label-desktop {
      display: inline !important;
    }
  }
  
  @media (max-width: 767px) {
    .footer-connection-time {
      display: none !important;
    }
    
    .footer-support {
      display: block !important;
      font-size: 10px !important;
    }
    
    .footer-support-label-desktop {
      display: none !important;
    }
    
    .footer-support-label-mobile {
      display: inline !important;
    }
    
    .terminal-footer {
      padding: 6px 12px !important;
      font-size: 10px !important;
      min-height: 28px !important;
    }
  }
  
  /* Prevent bounce scroll on iOS */
  body {
    position: fixed;
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
  }
  
  /* Fix for iOS Safari viewport height */
  @supports (-webkit-touch-callout: none) {
    .terminal-container {
      height: 100vh;
      height: -webkit-fill-available;
    }
  }
  
  /* Handle virtual keyboard on mobile */
  @media (max-width: 767px) {
    html {
      height: 100vh;
      height: -webkit-fill-available;
    }
    
    body {
      height: 100vh;
      height: -webkit-fill-available;
    }
    
    #root {
      height: 100vh;
      height: -webkit-fill-available;
    }
  }
  
  /* Hide scrollbars but keep functionality */
  .xterm .xterm-viewport::-webkit-scrollbar {
    display: none;
  }
  
  .xterm .xterm-viewport {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
